import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  // ENV
  const baseUrl = process.env.REACT_APP_SALES_API_BASE_URL;
  const username = process.env.REACT_APP_API_AUTH_USERNAME;
  const password = process.env.REACT_APP_API_AUTH_PASSWORD;
  const appName = process.env.REACT_APP_APP_NAME;

  // BASIC
  const basicAuth = "Basic " + btoa(`${username}:${password}`);

  // Global state
  const [loading, setLoading] = useState(false);
  const [Products, setProducts] = useState([]);
  const [team, setTeam] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  //  Admin state derived from session
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const adminId = currentAdmin?.adminId || "";
  const [userId, setUserId] = useState(null);

  // Axios instance (Basic)
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: baseUrl,
      headers: {
        "Content-Type": "application/json",
        Authorization: basicAuth,
        "X-App-Name": appName || "WebApp",
      },
    });

    instance.interceptors.request.use((config) => {
      try {
        const raw = sessionStorage.getItem("userData");
        if (raw) {
          const session = JSON.parse(raw);

          if (session?.adminMeta?.adminId) {
            config.headers["X-Admin-Id"] = String(session.adminMeta.adminId);
          }
        }
      } catch (_) {}
      return config;
    });

    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem("userData");
          toast.error("Session expired. Please log in again.");
          window.location.assign("/login");
        }
        return Promise.reject(err);
      }
    );

    return instance;
  }, [baseUrl, basicAuth, appName]);

  const GetProducts = useCallback(async () => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      const userId = parsed?.adminMeta?.adminId;

      if (!userId) {
        return;
      }

      setLoading(true);

      const payload = { adminId: userId };
      const res = await api.post(`/Admin/v1.0/getAllProducts`, payload);

      if (res?.data?.responseCode === "00") {
        setProducts(res.data.data || []);
      } else {
        throw new Error(
          res?.data?.responseMessage || "Failed to fetch products."
        );
      }
    } catch (e) {
      console.error(" GetProducts error:", e);
      if (!(e?.response?.status === 401)) {
        toast.error(e.message || "Something went wrong!");
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (raw) {
        const parsed = JSON.parse(raw);

        if (parsed?.adminMeta) {
          setCurrentAdmin(parsed.adminMeta);
        }

        const primaryId =
          parsed?.user?.id || parsed?.rawLoginResponse?.pkid || null;
        if (primaryId) {
          setUserId(String(primaryId));
        }
      } else {
      }
    } catch (err) {
      console.error(" Error loading session:", err);
    }
  }, []);

  useEffect(() => {
    if (currentAdmin?.adminId) {
      GetProducts();
    }
  }, [currentAdmin, GetProducts]);

  const refreshProducts = useCallback(() => {
    GetProducts();
  }, [GetProducts]);

  return (
    <GlobalContext.Provider
      value={{
        baseUrl,
        appName,
        team,
        setTeam,
        searchQuery,
        setSearchQuery,
        Products,
        loading,
        currentAdmin,
        adminId,
        setCurrentAdmin,
        id: userId,
        api,
        GetProducts,
        refreshProducts,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
